import React, { useState } from "react";
import { Box, Typography, Divider, useTheme, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import Header from "../../../components/Header";
import { useNavigate, useParams } from "react-router-dom";
import { useGetApplicationByIdQuery } from "../../../services/applicationService";
import ErrorComponent from "../../../components/ErrorComponent";
import LoadingComponent from "../../../components/LoadingComponent";
import { tokens } from "../../../theme";
import { DataGrid } from "@mui/x-data-grid";
import { getProductUOMByName } from "../../../constants/enums";
import { getApplicationWeatherByValue } from "../../../constants/enums";
import { formatDate } from "../../../utils/formatDate";
import SuccessButton from "../../../components/SuccessButton";
import { dataGridStyles, localeText } from "../../../utils/dataGridConfig";

const ViewApplication = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: applicationDetails, isLoading, error } = useGetApplicationByIdQuery(id);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [boomCapacity, setBoomCapacity] = useState(2000); // State for boom capacity

  if (isLoading) return <LoadingComponent />;
  if (error) return <ErrorComponent />;

  const dosagesWithIds = applicationDetails?.dosages?.map((dosage, index) => ({
    ...dosage,
    id: index,
  })) || [];

  const columns = [
    {
      field: "product_name",
      headerName: "Producto",
      flex: 1,
    },
    {
      field: "dosage_per_boom",
      headerName: "Cantidad por Boom",
      flex: 1,
      renderCell: (params) => {
        const dosageQuantity = params.row?.dosage_quantity || 0;
        const productUOM = params.row?.product_uom || "";
        const uomLabel = getProductUOMByName(productUOM)?.label || "";
        return `${dosageQuantity * (boomCapacity / 2000)} ${uomLabel}`; // Dynamic calculation based on boom capacity
      },
    },
    {
      field: "total_quantity",
      headerName: "Cantidad Total",
      flex: 1,
      renderCell: (params) => {
        const totalQuantity = params.row?.total_quantity || 0;
        const productUOM = params.row?.product_uom || "";
        const uomLabel = getProductUOMByName(productUOM)?.label || "";
        return `${totalQuantity} ${uomLabel}`;
      },
    },
  ];

  return (
    <Box m="20px">
      <Header title="Detalles de la Aplicación" />

      {/* Application Details Section */}
      <Box
        display="grid"
        gap="20px"
        gridTemplateColumns="repeat(3, minmax(0, 1fr))"
      >
        {/* Application Name */}
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
            Nombre de Aplicación
          </Typography>
          <Typography variant="body1" color={colors.grey[100]}>
            {applicationDetails?.name}
          </Typography>
        </Box>

        {/* Programmed Date */}
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
            Fecha Programada
          </Typography>
          <Typography variant="body1" color={colors.grey[100]}>
            {formatDate(applicationDetails?.programmed_date)}
          </Typography>
        </Box>

        {/* Applied Date */}
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
            Fecha Aplicada
          </Typography>
          <Typography variant="body1" color={colors.grey[100]}>
            {applicationDetails.applied_date ? formatDate(applicationDetails?.applied_date) : 'N/A'}
          </Typography>
        </Box>

        {/* Operator */}
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
            Operario
          </Typography>
          <Typography variant="body1" color={colors.grey[100]}>
            {applicationDetails.operator ? applicationDetails.operator : 'N/A'}
          </Typography>
        </Box>

        {/* Weather */}
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
            Clima
          </Typography>
          <Typography variant="body1" color={colors.grey[100]}>
            {getApplicationWeatherByValue(applicationDetails?.weather).label}
          </Typography>
        </Box>

        {/* Lote */}
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
            Lote
          </Typography>
          <Typography variant="body1" color={colors.grey[100]}>
            {applicationDetails?.lote_name}
          </Typography>
        </Box>

        {/* Total Area */}
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
            Hectáreas
          </Typography>
          <Typography variant="body1" color={colors.grey[100]}>
            {applicationDetails?.total_area}
          </Typography>
        </Box>

        {/* Location */}
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" color={colors.greenAccent[500]}>
            Bloques a aplicar
          </Typography>
          <Typography variant="body1" color={colors.grey[100]}>
            {applicationDetails?.location}
          </Typography>
        </Box>

        {/* Boom Capacity Selector */}
        <Box>
          <FormControl fullWidth size="small">
            <InputLabel id="boom-capacity-label" sx={{ color: colors.grey[100] }}>
              Capacidad del Boom
            </InputLabel>
            <Select
              labelId="boom-capacity-label"
              value={boomCapacity}
              label="Capacidad"
              onChange={(e) => setBoomCapacity(e.target.value)}
              sx={{
                backgroundColor: colors.primary[500],
                color: colors.grey[100],
                width: "120px",
                "& .MuiSelect-select": {
                  padding: "8px 12px",
                },
              }}
            >
              <MenuItem value={2000}>2000 L</MenuItem>
              <MenuItem value={4000}>4000 L</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Divider */}
      <Divider sx={{ my: "20px", borderColor: colors.grey[700] }} />

      {/* Dosage Table Section */}
      <Typography variant="h5" fontWeight="bold" color={colors.greenAccent[500]}>
        Dosificación
      </Typography>
      <Box height="40vh" sx={dataGridStyles(colors)}>
        <DataGrid
          rows={dosagesWithIds}
          columns={columns}
          getRowId={(row) => row.id}
          pageSizeOptions={[100]}
          loadingText="Cargando..."
          localeText={localeText}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnSorting
        />
      </Box>

      {/* Back Button */}
      <Box
        display="flex"
        justifyContent="end"
        gap="4px"
        mt="20px"
      >
        <SuccessButton label="Ver Aplicaciones" onClick={() => navigate('/applications')}/>
      </Box>
    </Box>
  );
};

export default ViewApplication;